import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { InitialFilter } from './AdvancedFilters';
import { buildRansackSearch } from './AdvancedFilters';
import type { FilterRowData, OperatorKey } from './filterTypes';

// AIDEV-NOTE: Operator suffixes ordered longest-first so the greedy match is unambiguous.
const OPERATOR_SUFFIXES: Array<{ suffix: string; operator: OperatorKey }> = [
  { operator: 'not_cont', suffix: '_not_cont' },
  { operator: 'not_eq', suffix: '_not_eq' },
  { operator: 'cont', suffix: '_cont' },
  { operator: 'eq', suffix: '_eq' },
];

export const parseFiltersFromParams = (searchParams: URLSearchParams): InitialFilter[] => {
  const filters: InitialFilter[] = [];

  searchParams.forEach((value, key) => {
    const match = OPERATOR_SUFFIXES.find(({ suffix }) => key.endsWith(suffix));
    if (!match) return;

    const property = key.slice(0, -match.suffix.length);
    if (property) {
      filters.push({ operator: match.operator, property, value });
    }
  });

  return filters;
};

export const serializeFiltersToParams = (
  rows: FilterRowData[],
  existing: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams();

  // Preserve non-filter params
  existing.forEach((value, key) => {
    const isFilterKey = OPERATOR_SUFFIXES.some(({ suffix }) => key.endsWith(suffix));
    if (!isFilterKey) {
      next.set(key, value);
    }
  });

  // Add filter params from rows
  const search = buildRansackSearch(rows);
  Object.entries(search).forEach(([key, value]) => {
    next.set(key, value);
  });

  return next;
};

interface UseFilterSearchParamsResult {
  initialFilters: InitialFilter[];
  syncRowsToURL: (rows: FilterRowData[]) => void;
}

const useFilterSearchParams = (): UseFilterSearchParamsResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const initialFiltersRef = useRef(parseFiltersFromParams(searchParams));

  const syncRowsToURL = useCallback((rows: FilterRowData[]) => {
    const next = serializeFiltersToParams(rows, searchParamsRef.current);
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  return {
    initialFilters: initialFiltersRef.current,
    syncRowsToURL,
  };
};

export default useFilterSearchParams;
